/**
 * TriggerPreviewModal Component
 *
 * Modal for previewing entities that match a trigger's configuration.
 * Shows how many entities would be affected by inactivity or date field triggers.
 *
 * Chrome via Modal shared (F72 — tema claro fixo + bottom-sheet mobile de graça);
 * paginação do rodapé no mini-padrão dos modais de log (F54).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalContent } from '@/shared/components/ui/Modal';
import { useTriggerPreview } from '../hooks/useTriggerPreview';
import type { TriggerPreviewRequest, TriggerPreviewEntity } from '../hooks/useTriggerPreview';
import { Icon } from '@/shared/icons'
interface TriggerPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  triggerType: 'trigger_inactivity' | 'trigger_date_field';
  config: Record<string, unknown>;
}

/**
 * Status badge color mapping
 */
const getStatusStyle = (status?: string): { bg: string; color: string } => {
  switch (status) {
    case 'active':
      return { bg: '#dcfce7', color: '#166534' };
    case 'waiting':
      return { bg: '#fef9c3', color: '#854d0e' };
    case 'closed':
    case 'resolved':
      return { bg: '#f3f4f6', color: '#374151' };
    case 'open':
      return { bg: '#dbeafe', color: '#1e40af' };
    default:
      return { bg: '#f1f5f9', color: '#64748b' };
  }
};

/** pt-BR labels for entity status shown in the preview badge (conversation/ticket/lead) */
const STATUS_LABELS: Record<string, string> = {
  active: 'Ativa',
  waiting: 'Aguardando',
  closed: 'Fechada',
  open: 'Aberto',
  pending: 'Pendente',
  paused: 'Pausado',
  resolved: 'Resolvido',
  canceled: 'Cancelado',
  new: 'Novo',
  contacted: 'Em contato',
  qualified: 'Qualificado',
  disqualified: 'Desqualificado',
  converted: 'Convertido',
  lost: 'Perdido',
};

/**
 * TriggerPreviewModal Component
 */
export const TriggerPreviewModal: React.FC<TriggerPreviewModalProps> = ({
  isOpen,
  onClose,
  triggerType,
  config,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const preview = useTriggerPreview();

  const fetchPreview = useCallback(
    (page: number) => {
      preview.mutate({
        triggerType,
        config,
        page,
        limit: 20,
      } as TriggerPreviewRequest);
    },
    [triggerType, config]
  );

  // Fetch on open and page change
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      fetchPreview(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && currentPage > 1) {
      fetchPreview(currentPage);
    }
  }, [currentPage]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((p) => p - 1);
    }
  };

  const handleNextPage = () => {
    if (preview.data && currentPage < preview.data.totalPages) {
      setCurrentPage((p) => p + 1);
    }
  };

  if (!isOpen) return null;

  const data = preview.data;
  const isLoading = preview.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="lg">
      <div data-testid="trigger-preview-modal">
        {/* Header (Modal shared — X padronizado, data-testid="modal-close") */}
        <ModalHeader onClose={onClose}>
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
            >
              <Icon name="eye" className="w-5 h-5" style={{ color: '#8b5cf6' }} />
            </div>
            <div>
              <h2
                className="text-lg font-semibold"
                style={{ color: '#1e293b' }}
              >
                Entidades do Trigger
              </h2>
              {data && !isLoading && (
                <p className="text-xs" style={{ color: '#64748b' }}>
                  {data.total} {data.total === 1 ? 'entidade encontrada' : 'entidades encontradas'}
                </p>
              )}
            </div>
          </div>
        </ModalHeader>

        {/* Content */}
        <ModalContent padding={false} className="max-h-[60vh] overflow-y-auto">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent" />
            </div>
          )}

          {/* Empty State */}
          {!isLoading && data && data.total === 0 && (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div
                className="p-3 rounded-full mb-3"
                style={{ backgroundColor: '#f1f5f9' }}
              >
                <Icon name="users" className="w-6 h-6" style={{ color: '#94a3b8' }} />
              </div>
              <p
                className="text-sm font-medium"
                style={{ color: '#1e293b' }}
              >
                Nenhuma entidade encontrada
              </p>
              <p
                className="text-xs mt-1 text-center"
                style={{ color: '#64748b' }}
              >
                Nenhuma entidade corresponde à configuração atual do trigger.
              </p>
            </div>
          )}

          {/* Entity List */}
          {!isLoading && data && data.entities.length > 0 && (
            <div>
              {data.entities.map((entity: TriggerPreviewEntity, index: number) => (
                <div
                  key={entity.id}
                  className={`px-4 py-3 flex items-center gap-3 ${index > 0 ? 'border-t' : ''}`}
                  style={index > 0 ? { borderColor: '#f1f5f9' } : {}}
                >
                  {/* Entity avatar */}
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium"
                    style={{ backgroundColor: '#ede9fe', color: '#7c3aed' }}
                  >
                    {entity.name.charAt(0).toUpperCase()}
                  </div>

                  {/* Entity info */}
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-medium truncate"
                      style={{ color: '#1e293b' }}
                    >
                      {entity.name}
                    </p>
                    <p
                      className="text-xs truncate"
                      style={{ color: '#64748b' }}
                    >
                      {entity.subtitle}
                    </p>
                  </div>

                  {/* Status badge */}
                  {entity.status && (
                    <span
                      className="px-2 py-0.5 text-xs rounded-full flex-shrink-0"
                      style={{
                        backgroundColor: getStatusStyle(entity.status).bg,
                        color: getStatusStyle(entity.status).color,
                      }}
                    >
                      {STATUS_LABELS[entity.status] ?? entity.status}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Error State */}
          {!isLoading && preview.isError && (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <p
                className="text-sm font-medium"
                style={{ color: '#ef4444' }}
              >
                Erro ao carregar preview
              </p>
              <p
                className="text-xs mt-1 text-center"
                style={{ color: '#64748b' }}
              >
                Não foi possível buscar as entidades. Tente novamente.
              </p>
              <button
                className="mt-3 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                style={{
                  backgroundColor: '#8b5cf6',
                  color: '#ffffff',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#7c3aed';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#8b5cf6';
                }}
                onClick={() => fetchPreview(currentPage)}
              >
                Tentar novamente
              </button>
            </div>
          )}
        </ModalContent>

        {/* Footer com paginação — F54: mini-padrão dos modais de log
            (IGUAL ao WebhookLogsModal/CustomActionLogsModal: sem ícone,
            text-xs, "Página X de Y (Z registros)") */}
        {data && data.totalPages > 1 && !isLoading && (
          <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: '#e5e7eb' }}>
            <p className="text-xs" style={{ color: '#64748b' }}>
              Página {currentPage} de {data.totalPages} ({data.total} registros)
            </p>
            <div className="flex gap-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage <= 1}
                className="px-3 py-1 text-xs rounded border transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb', color: '#1e293b' }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
                data-testid="trigger-preview-prev"
              >
                Anterior
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage >= data.totalPages}
                className="px-3 py-1 text-xs rounded border transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb', color: '#1e293b' }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
                data-testid="trigger-preview-next"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TriggerPreviewModal;
